/**
 * Link Builder Utilities
 * Helper functions to create navigation links easily
 */

/**
 * Creates a simple navigation link
 * @param {Object} config - Link configuration
 * @param {string} config.title - Link title
 * @param {string} config.path - Link path
 * @param {React.Component} config.icon - Lucide icon component
 * @param {string} [config.badge] - Optional badge text
 * @returns {Object} Navigation link object
 */
export const createNavLink = ({ title, path, icon, badge = null }) => ({
  title,
  path,
  icon,
  badge,
  children: [],
});

/**
 * Creates a navigation link with children/sub-items
 * @param {Object} config - Link configuration
 * @param {string} config.title - Link title
 * @param {string} config.path - Link path
 * @param {React.Component} config.icon - Lucide icon component
 * @param {Array} config.children - Array of child links
 * @param {string} [config.badge] - Optional badge text
 * @returns {Object} Navigation link object with children
 */
export const createNavLinkWithChildren = ({ title, path, icon, children = [], badge = null }) => ({
  title,
  path,
  icon,
  badge,
  children: Array.isArray(children) ? children : [],
});

/**
 * Creates a child navigation link
 * @param {Object} config - Child link configuration
 * @param {string} config.title - Link title
 * @param {string} config.path - Link path
 * @param {React.Component} config.icon - Lucide icon component
 * @param {string} [config.badge] - Optional badge text
 * @returns {Object} Child navigation link object
 */
export const createChildLink = ({ title, path, icon, badge = null }) => ({
  title,
  path,
  icon,
  badge,
});

/**
 * Creates multiple child links at once
 * @param {Array} childrenConfigs - Array of child link configurations
 * @returns {Array} Array of child navigation links
 */
export const createChildLinks = (childrenConfigs) => {
  return childrenConfigs.map(config => createChildLink(config));
};

/**
 * Helper to add a badge to an existing link
 * @param {Object} link - Existing link object
 * @param {string} badge - Badge text
 * @returns {Object} Link with badge added
 */
export const addBadge = (link, badge) => ({
  ...link,
  badge,
});

/**
 * Helper to add children to an existing link
 * @param {Object} link - Existing link object
 * @param {Array} children - Array of child links
 * @returns {Object} Link with children added
 */
export const addChildren = (link, children) => ({
  ...link,
  children: Array.isArray(children) ? children : [],
});

